import { useMemo } from 'react';
import { Toast } from '@base-ui/react/toast';

/**
 * Thin wrapper over Base UI's toast manager. Call from inside a
 * `<ToastProvider>` (see components/ui/Toast.tsx) — mounted once near the
 * app root alongside the Toaster that renders the queued toasts.
 */
export function useToast() {
  const manager = Toast.useToastManager();
  // `manager` is re-memoized by Base UI whenever the toast list changes
  // (it's keyed on `[toasts, store]`), so it isn't stable across renders.
  // `manager.add` is a class-field arrow function assigned once on the
  // underlying store instance, which itself is created once per
  // ToastProvider via a ref — so it keeps a stable identity for the
  // provider's lifetime. Keying the memo on it (rather than on `manager`)
  // keeps the returned helpers' identities stable too, so a consumer that
  // later puts one in a dependency array won't loop.
  return useMemo(() => {
    const push = (type: 'success' | 'error' | 'info' | 'warning') => (message: string) =>
      void manager.add({ description: message, type, timeout: 6000 });
    return {
      success: push('success'),
      error: push('error'),
      info: push('info'),
      warning: push('warning'),
    };
    // `manager` itself is re-memoized on every toast add/close; `manager.add`
    // is the part that's actually stable, so it's the correct dependency —
    // depending on `manager` instead would defeat this memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manager.add]);
}
